import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import capitalize from 'lodash.capitalize';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Input, Textarea } from '@feathr/components';

import * as styles from './InputListItem.css';

export interface IInputListItemSharedProps {
  maxLength: number;
  label?: string;
  onChange: (index: number, value: string) => void;
  onRemove?: (index: number) => void;
  removeTooltip: string;
  type?: 'input' | 'textarea';
}

interface IInputListItemProps extends IInputListItemSharedProps {
  canRemove: boolean;
  item: string;
  index: number;
  validationError?: string[];
}

function InputListItem({
  canRemove,
  index,
  item,
  maxLength,
  onChange,
  onRemove,
  label,
  removeTooltip,
  type = 'input',
  validationError,
}: Readonly<IInputListItemProps>): JSX.Element {
  const { t } = useTranslation();
  const [isTouched, setIsTouched] = useState(false);

  function handleRemove(): void {
    onRemove && onRemove(index);
  }

  function handleChange(value: string | undefined): void {
    const newValue = value ?? '';
    setIsTouched(true);
    onChange(index, newValue);
  }

  /*
   * Leaving the field counts as having dealt with it, so a row left blank is flagged on the
   * way out. Typing counts too: clearing a row out should flag it without also having to
   * leave, and the message would otherwise sit there until focus moved on.
   */
  function handleBlur(): void {
    setIsTouched(true);
  }

  /*
   * A blank row the editor seeded and nobody has been near is a placeholder, not a mistake:
   * a new ad starts with three headlines and two descriptions, and flagging every one of
   * them on arrival is noise rather than guidance. A row that already holds text is
   * different — a problem with it, a repeat say, is worth showing straight away whether or
   * not this session typed it.
   */
  const isUntouchedPlaceholder = !isTouched && !item.trim();
  const shownValidationError = isUntouchedPlaceholder ? undefined : validationError;

  const sharedProps = {
    label: `${capitalize(label)} ${index + 1}`,
    maxLength: maxLength,
    onBlur: handleBlur,
    onChange: handleChange,
    showCounter: true,
    validationError: shownValidationError,
    value: item,
  };

  return (
    <div className={styles.root}>
      {type === 'input' ? (
        /*
         * `initializeFocused` because Input otherwise withholds a `validationError` until
         * the field has been focused, and a problem on an already-saved ad would stay
         * hidden. Textarea has no such gate, so the `isTouched` check above is what keeps
         * the two behaving the same.
         */
        <Input {...sharedProps} initializeFocused={!!shownValidationError?.length} />
      ) : (
        <Textarea {...sharedProps} />
      )}
      {onRemove && (
        <Button
          // An icon-only button has no text to name it, so spell the target out for screen readers.
          aria-label={t('Remove {{label}} {{number}}', { label, number: index + 1 })}
          disabled={!canRemove}
          onClick={handleRemove}
          tooltip={!canRemove && removeTooltip}
          type={'icon'}
        >
          <FontAwesomeIcon icon={faTrash} />
        </Button>
      )}
    </div>
  );
}

export default observer(InputListItem);
